
import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormValues } from './types';
import { supabase } from '@/lib/supabase';

interface ConversionFieldsProps {
  form: UseFormReturn<FormValues>;
  enableConversion: boolean;
  setEnableConversion: (value: boolean) => void;
}

interface UnitOption {
  value: string;
  label: string;
  symbol: string;
  is_default?: boolean;
}

const ConversionFields: React.FC<ConversionFieldsProps> = ({
  form,
  enableConversion,
  setEnableConversion,
}) => {
  const [customUnitOptions, setCustomUnitOptions] = useState<UnitOption[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Get user profile for company_id
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    fetchUserProfile();
  }, []);

  // Fetch available units
  useEffect(() => {
    if (!profile?.company_id) return;
    
    const fetchUnits = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .rpc('get_all_units', { user_company_id: profile.company_id })
          .order('is_default', { ascending: false })
          .order('name', { ascending: true });
          
        if (error) throw error;
        
        if (data) {
          // Transform to unit options format
          const options = data.map(unit => ({
            value: unit.name.toLowerCase().replace(/\s+/g, '_'),
            label: unit.name,
            symbol: unit.symbol,
            is_default: unit.is_default
          }));
          
          setCustomUnitOptions(options);
        }
      } catch (error) {
        console.error('Error loading units:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUnits();
  }, [profile]);

  return (
    <div className="space-y-4">
      <div className="flex items-start">
        <Checkbox
          id="enable-conversion"
          name="enable-conversion"
          checked={enableConversion}
          onCheckedChange={(checked) => {
            const isChecked = checked === true;
            console.log("Conversion checkbox changed:", isChecked);
            setEnableConversion(isChecked);
            if (!isChecked) {
              form.setValue('conversion_unit', '');
              form.setValue('conversion_factor', undefined);
            }
          }}
        />
        <label
          htmlFor="enable-conversion"
          className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Enable Unit Conversion
        </label>
      </div>

      {enableConversion && (
        <div className="flex flex-wrap gap-4">
          <FormField
            control={form.control}
            name="conversion_unit"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel htmlFor="conversion_unit">Conversion Unit</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  name="conversion_unit"
                >
                  <FormControl>
                    <SelectTrigger id="conversion_unit">
                      <SelectValue placeholder={loading ? "Loading units..." : "Select unit"} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {customUnitOptions.length > 0 ? (
                      customUnitOptions.map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.symbol} {unit.is_default && '(Default)'}
                        </SelectItem>
                      ))
                    ) : loading ? (
                      <SelectItem value="loading" disabled>Loading units...</SelectItem>
                    ) : (
                      <SelectItem value="" disabled>No units available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="conversion_factor"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel htmlFor="conversion_factor">Conversion Factor</FormLabel>
                <FormControl>
                  <Input 
                    id="conversion_factor"
                    name="conversion_factor"
                    type="number" 
                    step="0.01" 
                    {...field} 
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val === '' ? '' : parseFloat(val));
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        field.onChange(undefined);
                      }
                    }}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ConversionFields;
